// VoiceInput — mic-primary input for the void interface
//
// Default mode: microphone. User speaks, Web Speech API transcribes,
// transcript submitted on natural pause (~1-1.5s of silence, handled by browser VAD).
//
// Keyboard toggle: small icon next to the mic reveals a text field.
//
// Props:
//   onSubmit(text)  — called with final transcript or typed text
//   autoListen      — when true (uiPhase === 'listening'), mic starts automatically
//   disabled        — blocks all interaction (thinking / shattering / init)

import { useState, useRef, useEffect, useCallback } from 'react';
import { unlockAudioContext } from '../../hooks/useTTS.js';
import './VoiceInput.css';

// ── Speech Recognition ────────────────────────────────────────────────────────
const SR = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition || null)
  : null;

// ── Icons ─────────────────────────────────────────────────────────────────────

function MicIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8"  y1="23" x2="16" y2="23"/>
    </svg>
  );
}

function KeyboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <rect x="2" y="6" width="20" height="12" rx="2"/>
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8"/>
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function VoiceInput({ onSubmit, autoListen, disabled, onModeChange }) {
  const [inputMode,   setInputMode]   = useState('voice');  // 'voice' | 'keyboard'
  const [isListening, setIsListening] = useState(false);
  const [interim,     setInterim]     = useState('');
  const [textValue,   setTextValue]   = useState('');

  const recognitionRef = useRef(null);
  const listeningRef   = useRef(false);   // stable flag — avoids stale closures
  const onSubmitRef    = useRef(onSubmit);
  const textInputRef   = useRef(null);

  // Keep onSubmit ref current
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  // ── Initialise Speech Recognition ─────────────────────────────────────────
  useEffect(() => {
    if (!SR) return;

    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = 'en-US';

    let final = '';
    let interim_ = '';

    r.onresult = (e) => {
      final   = '';
      interim_ = '';
      for (const res of e.results) {
        if (res.isFinal) final   += res[0].transcript;
        else             interim_ += res[0].transcript;
      }
      setInterim(interim_ || final);
    };

    r.onend = () => {
      setIsListening(false);
      const transcript = (final || interim_).trim();
      console.log('[VoiceInput] recognition end, transcript:', JSON.stringify(transcript), 'listening:', listeningRef.current);
      final   = '';
      interim_ = '';
      setInterim('');

      if (transcript && listeningRef.current) {
        // Got speech — submit and stop auto-restart
        listeningRef.current = false;
        console.log('[VoiceInput] submitting:', transcript);
        onSubmitRef.current(transcript);
      } else if (listeningRef.current) {
        // No speech detected — restart to keep listening
        try { r.start(); setIsListening(true); } catch (err) { console.warn('[VoiceInput] restart failed:', err); }
      }
    };

    r.onerror = (e) => {
      console.warn('[VoiceInput] recognition error:', e.error);
      if (e.error === 'no-speech' && listeningRef.current) {
        // Browser timed out waiting for speech — restart silently
        final   = '';
        interim_ = '';
        setInterim('');
        try { r.start(); } catch {}
      } else if (e.error !== 'aborted') {
        setIsListening(false);
        listeningRef.current = false;
        setInterim('');
      }
    };

    recognitionRef.current = r;
    return () => {
      listeningRef.current = false;
      r.abort();
      recognitionRef.current = null;
    };
  }, []); // stable — SR never changes

  // ── Auto-listen: start/stop based on prop ─────────────────────────────────
  useEffect(() => {
    if (inputMode !== 'voice') return;
    if (!SR || !recognitionRef.current) return;

    if (autoListen && !disabled && !listeningRef.current) {
      listeningRef.current = true;
      try {
        recognitionRef.current.start();
        setIsListening(true);
        setInterim('');
        console.log('[VoiceInput] autoListen → mic started');
      } catch (err) {
        console.warn('[VoiceInput] autoListen start failed:', err.message);
      }
    } else if ((!autoListen || disabled) && listeningRef.current) {
      listeningRef.current = false;
      recognitionRef.current.abort();
      setIsListening(false);
      setInterim('');
    }
  }, [autoListen, disabled, inputMode]);

  // ── Manual mic toggle ─────────────────────────────────────────────────────
  const toggleMic = useCallback(() => {
    if (disabled || !SR || !recognitionRef.current) return;
    unlockAudioContext(); // ensure AudioContext is running for TTS playback
    if (isListening) {
      listeningRef.current = false;
      recognitionRef.current.abort();
      setIsListening(false);
      setInterim('');
    } else {
      listeningRef.current = true;
      try {
        recognitionRef.current.start();
        setIsListening(true);
        setInterim('');
      } catch {}
    }
  }, [disabled, isListening]);

  // ── Switch to keyboard ────────────────────────────────────────────────────
  const switchToKeyboard = useCallback(() => {
    if (recognitionRef.current && listeningRef.current) {
      listeningRef.current = false;
      recognitionRef.current.abort();
      setIsListening(false);
      setInterim('');
    }
    setInputMode('keyboard');
    onModeChange?.('keyboard');
  }, [onModeChange]);

  // ── Switch to voice ───────────────────────────────────────────────────────
  const switchToVoice = useCallback(() => {
    setInputMode('voice');
    setTextValue('');
    onModeChange?.('voice');
  }, [onModeChange]);

  // ── Text submit ───────────────────────────────────────────────────────────
  const handleTextSubmit = useCallback((e) => {
    e.preventDefault();
    const v = textValue.trim();
    if (!v || disabled) return;
    setTextValue('');
    onSubmitRef.current(v);
  }, [textValue, disabled]);

  // ── Auto-focus text input ─────────────────────────────────────────────────
  useEffect(() => {
    if (inputMode === 'keyboard' && !disabled) {
      const t = setTimeout(() => textInputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [inputMode, disabled]);

  const micSupported = Boolean(SR);

  // ── Keyboard mode ─────────────────────────────────────────────────────────
  if (inputMode === 'keyboard') {
    return (
      <div className="vi-wrap vi-keyboard-mode">
        <form className="vi-text-form" onSubmit={handleTextSubmit}>
          <input
            ref={textInputRef}
            className="vi-text-input"
            type="text"
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
            placeholder="Speak..."
            disabled={disabled}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            enterKeyHint="send"
            aria-label="Your message"
          />
        </form>
        {micSupported && (
          <button
            className="vi-mode-toggle"
            onClick={switchToVoice}
            aria-label="Switch to voice"
            title="Switch to voice"
            type="button"
          >
            <MicIcon />
          </button>
        )}
      </div>
    );
  }

  // ── Voice mode ────────────────────────────────────────────────────────────
  return (
    <div className="vi-wrap vi-voice-mode">
      {/* Interim transcript floats above the mic */}
      {interim && (
        <div className="vi-interim" aria-live="polite">{interim}</div>
      )}

      <div className="vi-controls">
        <button
          className={[
            'vi-mic-btn',
            isListening  ? 'vi-mic-listening' : '',
            disabled     ? 'vi-mic-disabled'  : '',
          ].join(' ').trim()}
          onClick={toggleMic}
          disabled={disabled}
          aria-label={isListening ? 'Stop listening' : 'Tap to speak'}
          title={isListening ? 'Listening…' : 'Tap to speak'}
          type="button"
        >
          <MicIcon />
        </button>

        <button
          className="vi-mode-toggle"
          onClick={switchToKeyboard}
          aria-label="Switch to keyboard"
          title="Type instead"
          type="button"
        >
          <KeyboardIcon />
        </button>
      </div>

      {/* Screen reader: listening status */}
      <span className="sr-only" aria-live="assertive">
        {isListening ? 'Listening' : ''}
      </span>
    </div>
  );
}
